import React, { useState, useEffect } from 'react';
import { getReminderSettings, saveReminderSettings, requestPermission, scheduleReminders, hasNotificationSupport } from '../utils/notifications';
import type { ReminderSettings } from '../utils/notifications';
import { getProfile, saveProfile, getMedications, saveMedication, deleteMedication, saveWeeklyAssessment } from '../db/database';
import { setClientId, getClientId, startAutoSync, stopAutoSync, isGoogleDriveConfigured, initGoogleDrive, uploadToGoogleDrive } from '../utils/gdrive';
import { exportAllData } from '../db/database';
import { ITALIAN_REGIONS, KNOWN_ALLERGENS } from '../types';
import type { UserProfile, Medication, WeeklyAssessment } from '../types';
import { format, startOfWeek } from 'date-fns';

export default function Settings() {
  const [profile, setProfile] = useState<UserProfile>({
    name: '',
    region: '',
    knownAllergies: [],
    medications: [],
    latitude: null,
    longitude: null,
    googleDriveSyncEnabled: false,
    lastSyncTime: null,
    syncIntervalMinutes: 60,
  });
  const [meds, setMeds] = useState<Medication[]>([]);
  const [newMed, setNewMed] = useState({ name: '', type: 'antihistamine' as Medication['type'], timing: 'reactive' as Medication['timing'] });
  const [gdClientId, setGdClientId] = useState(getClientId() || '');
  const [showWeekly, setShowWeekly] = useState(false);
  const [weeklyRCAT, setWeeklyRCAT] = useState<number[]>([3, 3, 3, 3, 3, 3]);
  const [saved, setSaved] = useState(false);
  const [reminders, setReminders] = useState<ReminderSettings>(getReminderSettings());
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    hasNotificationSupport() ? Notification.permission : 'denied'
  );
  const [locationStatus, setLocationStatus] = useState('');

  useEffect(() => {
    async function load() {
      const p = await getProfile();
      if (p) setProfile(p);
      const m = await getMedications();
      setMeds(m);
    }
    load();
  }, []);

  const handleSaveProfile = async () => {
    await saveProfile(profile);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);

    // Setup auto-sync if enabled
    if (profile.googleDriveSyncEnabled && isGoogleDriveConfigured()) {
      const syncFn = async () => {
        try {
          await initGoogleDrive();
          const data = await exportAllData();
          await uploadToGoogleDrive(data);
        } catch (e) {
          console.error('Auto-sync failed:', e);
        }
      };
      startAutoSync(syncFn, profile.syncIntervalMinutes);
    } else {
      stopAutoSync();
    }
  };

  const handleAddMed = async () => {
    if (!newMed.name.trim()) return;
    const id = await saveMedication(newMed as Medication);
    setMeds([...meds, { ...newMed, id } as Medication]);
    setNewMed({ name: '', type: 'antihistamine', timing: 'reactive' });
  };

  const handleDeleteMed = async (id: number) => {
    await deleteMedication(id);
    setMeds(meds.filter(m => m.id !== id));
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('Geolocalizzazione non supportata');
      return;
    }
    setLocationStatus('Rilevamento posizione...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setProfile(prev => ({
          ...prev,
          latitude: Math.round(pos.coords.latitude * 10000) / 10000,
          longitude: Math.round(pos.coords.longitude * 10000) / 10000,
        }));
        setLocationStatus('Posizione rilevata!');
      },
      (err) => {
        setLocationStatus('Errore: ' + err.message);
      }
    );
  };

  const handleSaveGDriveConfig = () => {
    setClientId(gdClientId);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSaveWeeklyAssessment = async () => {
    const assessment: WeeklyAssessment = {
      weekStartDate: format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      createdAt: new Date().toISOString(),
      rcatScores: weeklyRCAT,
      rcatTotal: weeklyRCAT.reduce((s, v) => s + v, 0),
    };
    await saveWeeklyAssessment(assessment);
    setShowWeekly(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="page">
      <h1 className="page-title">Impostazioni</h1>

      {saved && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--success)', color: 'white', padding: '8px 24px',
          borderRadius: 20, fontSize: 13, fontWeight: 600, zIndex: 300
        }}>
          Salvato!
        </div>
      )}

      {/* Profile */}
      <div className="card">
        <div className="card-title">Profilo utente</div>
        <div className="form-group">
          <label className="form-label">Nome</label>
          <input className="form-input" value={profile.name}
            onChange={e => setProfile({ ...profile, name: e.target.value })}
            placeholder="Il tuo nome" />
        </div>
        <div className="form-group">
          <label className="form-label">Regione</label>
          <select className="form-select" value={profile.region}
            onChange={e => setProfile({ ...profile, region: e.target.value })}>
            <option value="">Seleziona regione</option>
            {ITALIAN_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Allergie note</label>
          <div className="chip-group">
            {KNOWN_ALLERGENS.map(a => (
              <button key={a}
                className={`chip ${profile.knownAllergies.includes(a) ? 'active' : ''}`}
                onClick={() => {
                  const allergies = profile.knownAllergies.includes(a)
                    ? profile.knownAllergies.filter(x => x !== a)
                    : [...profile.knownAllergies, a];
                  setProfile({ ...profile, knownAllergies: allergies });
                }}>
                {a}
              </button>
            ))}
          </div>
        </div>

        <button className="btn btn-primary btn-full" onClick={handleSaveProfile}>
          Salva profilo
        </button>
      </div>

      {/* Location */}
      <div className="card">
        <div className="card-title">Posizione (per dati meteo e pollini)</div>
        {profile.latitude && profile.longitude ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
            Lat: {profile.latitude}, Lon: {profile.longitude}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            Nessuna posizione configurata
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={handleGetLocation}>
            Rileva posizione
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <input className="form-input" placeholder="Lat" type="number" step="0.0001"
                value={profile.latitude ?? ''} style={{ width: '50%' }}
                onChange={e => setProfile({ ...profile, latitude: e.target.value ? parseFloat(e.target.value) : null })} />
              <input className="form-input" placeholder="Lon" type="number" step="0.0001"
                value={profile.longitude ?? ''} style={{ width: '50%' }}
                onChange={e => setProfile({ ...profile, longitude: e.target.value ? parseFloat(e.target.value) : null })} />
            </div>
          </div>
        </div>
        {locationStatus && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{locationStatus}</div>
        )}
      </div>

      {/* Medications */}
      <div className="card">
        <div className="card-title">Farmaci</div>
        {meds.map(m => (
          <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{m.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {m.type === 'antihistamine' ? 'Antistaminico' : m.type === 'nasal_spray' ? 'Spray nasale' : m.type === 'eye_drops' ? 'Collirio' : m.type === 'inhaler' ? 'Inalatore' : m.type === 'immunotherapy' ? 'Immunoterapia' : 'Altro'}
                {' - '}
                {m.timing === 'prophylactic' ? 'Profilattico' : 'Al bisogno'}
              </div>
            </div>
            <button className="btn btn-sm btn-danger" onClick={() => handleDeleteMed(m.id!)}>X</button>
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          <div className="form-group">
            <input className="form-input" placeholder="Nome farmaco"
              value={newMed.name} onChange={e => setNewMed({ ...newMed, name: e.target.value })} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select className="form-select" value={newMed.type}
              onChange={e => setNewMed({ ...newMed, type: e.target.value as any })}>
              <option value="antihistamine">Antistaminico</option>
              <option value="nasal_spray">Spray nasale</option>
              <option value="eye_drops">Collirio</option>
              <option value="inhaler">Inalatore</option>
              <option value="immunotherapy">Immunoterapia</option>
              <option value="other">Altro</option>
            </select>
            <select className="form-select" value={newMed.timing}
              onChange={e => setNewMed({ ...newMed, timing: e.target.value as any })}>
              <option value="reactive">Al bisogno</option>
              <option value="prophylactic">Profilattico</option>
            </select>
          </div>
          <button className="btn btn-secondary btn-full" onClick={handleAddMed}>
            Aggiungi farmaco
          </button>
        </div>
      </div>

      {/* Google Drive */}
      <div className="card">
        <div className="card-title">Google Drive Sync</div>
        <div className="form-group">
          <label className="form-label">OAuth Client ID</label>
          <input className="form-input" value={gdClientId}
            onChange={e => setGdClientId(e.target.value)}
            placeholder="xxxxx.apps.googleusercontent.com" />
        </div>
        <div className="toggle-row">
          <span className="toggle-label">Sincronizzazione automatica</span>
          <div className={`toggle ${profile.googleDriveSyncEnabled ? 'on' : ''}`}
            onClick={() => setProfile({ ...profile, googleDriveSyncEnabled: !profile.googleDriveSyncEnabled })} />
        </div>
        {profile.googleDriveSyncEnabled && (
          <div className="form-group" style={{ marginTop: 8 }}>
            <label className="form-label">Intervallo sincronizzazione (minuti)</label>
            <select className="form-select" value={profile.syncIntervalMinutes}
              onChange={e => setProfile({ ...profile, syncIntervalMinutes: parseInt(e.target.value) })}>
              <option value="30">30 minuti</option>
              <option value="60">1 ora</option>
              <option value="360">6 ore</option>
              <option value="720">12 ore</option>
              <option value="1440">24 ore</option>
            </select>
          </div>
        )}
        <button className="btn btn-secondary btn-full" onClick={handleSaveGDriveConfig} style={{ marginTop: 8 }}>
          Salva configurazione Google Drive
        </button>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Per ottenere un Client ID: console.cloud.google.com &gt; API &gt; Credenziali &gt; OAuth 2.0 &gt;
          Applicazione web. Aggiungi il dominio dell'app come origine autorizzata.
        </div>
      </div>

      {/* Weekly assessment */}
      <div className="card">
        <div className="card-title">Valutazione settimanale</div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Compila RCAT e ACT per un monitoraggio clinico completo.
        </p>
        <button className="btn btn-secondary btn-full" onClick={() => setShowWeekly(!showWeekly)}>
          {showWeekly ? 'Chiudi' : 'Compila valutazione settimanale'}
        </button>

        {showWeekly && (
          <div style={{ marginTop: 16 }}>
            {/* RCAT */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                RCAT - Rhinitis Control Assessment Test
              </h3>
              {[
                'Congestione nasale nell\'ultima settimana',
                'Starnuti nell\'ultima settimana',
                'Naso che cola nell\'ultima settimana',
                'Prurito nasale nell\'ultima settimana',
                'Difficolta\' a dormire per sintomi nasali',
                'Limitazione nelle attivita\'',
              ].map((q, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>{q}</div>
                  <div className="severity-selector">
                    {[1, 2, 3, 4, 5].map(v => (
                      <button key={v}
                        className={`severity-btn ${weeklyRCAT[i] === v ? `selected-${v <= 2 ? 3 : v <= 4 ? 1 : 0}` : ''}`}
                        onClick={() => {
                          const n = [...weeklyRCAT];
                          n[i] = v;
                          setWeeklyRCAT(n);
                        }}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Totale RCAT: {weeklyRCAT.reduce((s, v) => s + v, 0)}/30
                {weeklyRCAT.reduce((s, v) => s + v, 0) <= 21 ? ' - NON CONTROLLATO' : ' - Controllato'}
              </div>
            </div>

            <button className="btn btn-primary btn-full" style={{ marginTop: 16 }} onClick={handleSaveWeeklyAssessment}>
              Salva valutazione settimanale
            </button>
          </div>
        )}
      </div>

      {/* Promemoria */}
      <div className="card">
        <div className="card-title">Promemoria giornalieri</div>
        {!hasNotificationSupport() ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Le notifiche non sono supportate da questo browser.
          </div>
        ) : (
          <>
            {notifPermission === 'denied' && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>
                Le notifiche sono bloccate. Abilitale nelle impostazioni del browser.
              </div>
            )}
            {notifPermission !== 'granted' && notifPermission !== 'denied' && (
              <button className="btn btn-secondary" style={{ marginBottom: 12, fontSize: 13 }}
                onClick={async () => {
                  const granted = await requestPermission();
                  setNotifPermission(granted ? 'granted' : 'denied');
                }}>
                Richiedi permesso notifiche
              </button>
            )}
            <div className="toggle-row">
              <span className="toggle-label">Promemoria attivi</span>
              <div
                className={`toggle ${reminders.enabled ? 'on' : ''}`}
                onClick={() => setReminders(r => ({ ...r, enabled: !r.enabled }))}
              />
            </div>
            {reminders.enabled && (
              <>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label">Orario mattina</label>
                  <input
                    type="time"
                    className="form-input"
                    value={reminders.morningTime}
                    onChange={e => setReminders(r => ({ ...r, morningTime: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Orario sera</label>
                  <input
                    type="time"
                    className="form-input"
                    value={reminders.eveningTime}
                    onChange={e => setReminders(r => ({ ...r, eveningTime: e.target.value }))}
                  />
                </div>
              </>
            )}
            <button
              className="btn btn-primary btn-full"
              style={{ marginTop: 12 }}
              disabled={notifPermission !== 'granted'}
              onClick={async () => {
                saveReminderSettings(reminders);
                await scheduleReminders(reminders);
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);
              }}
            >
              {saved ? 'Salvato!' : 'Salva promemoria'}
            </button>
            {notifPermission === 'granted' && !hasNotificationSupport() === false && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                {typeof (window as any).TimestampTrigger !== 'undefined'
                  ? 'Notifiche programmate (funzionano anche con app chiusa).'
                  : 'Notifiche via timeout (richiedono la tab aperta).'}
              </div>
            )}
          </>
        )}
      </div>

      {/* App info */}
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>AllergoAway</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Tracker personale allergie - Italia
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Dati salvati localmente nel browser. Nessun server.
        </div>
      </div>
    </div>
  );
}
